import { useCan } from '@features/auth';
import { useClients, type ClientTarget } from '@features/clients';
import { cn } from '@lib/cn';
import {
    Badge,
    Button,
    Card,
    CardHeader,
    EmptyState,
    ErrorState,
    LoadingRows,
    SelectField,
    WriteError,
} from '@shared/ui';
import { useEffect, useId, useMemo, useState } from 'react';

import {
    readFailureOf,
    useDonationsReconciliation,
    useRequestDonationsReconciliation,
} from '../hooks/use-donations-reconciliation';
import {
    amount,
    canRequest,
    codeCounts,
    countGroups,
    fiscalCalendarNotice,
    issueLine,
    moneyCompared,
    panelState,
    refreshNotice,
    stateAnnouncement,
    totalsLines,
    truncationNotices,
    type ReconciliationPanelState,
} from '../model/donations-reconciliation';

import { Td, Th } from './table';

import type { DonationsReconciliationReport } from '../schemas/donations-reconciliation';

const NO_CLIENT = '';

/** A clock for staleness, ticking every 15 s. */
function useNow(): number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = setInterval(() => {
            setNow(Date.now());
        }, 15_000);
        return () => {
            clearInterval(timer);
        };
    }, []);
    return now;
}

/**
 * The donations reconciliation of one client (INI-0034 S6/S7): Salesforce compared
 * with Veevart, exactly, on what the Donations migration copied. Read-only on both
 * systems: it corrects nothing, and every difference is listed for a person to act on.
 *
 * Reading it is `read-only`; asking for a new one is `engineer` (it reads the org's
 * Salesforce and queues work on the runner), like an analysis.
 */
export function DonationsReconciliationPanel({ target }: { target: ClientTarget }) {
    const canWrite = useCan('engineer');
    const clients = useClients(target);
    const [tenantId, setTenantId] = useState<string>(NO_CLIENT);
    const chosen = tenantId === NO_CLIENT ? null : tenantId;
    const latest = useDonationsReconciliation(target, chosen);
    const request = useRequestDonationsReconciliation(target, chosen);
    const now = useNow();
    const statusId = useId();

    const options = useMemo(
        () => [
            { value: NO_CLIENT, label: 'Choose a client…' },
            ...[...(clients.data ?? [])]
                .sort((a, b) => a.displayName.localeCompare(b.displayName))
                .map((client) => ({
                    value: client.tenantId,
                    label: client.slug
                        ? `${client.displayName} (${client.slug})`
                        : client.displayName,
                })),
        ],
        [clients.data],
    );

    // react-query keeps `data` beside `error` after a failed re-read: the document
    // wins, and the failure is only the non-blocking refresh notice below.
    const failure = latest.isError ? readFailureOf(latest.error) : null;
    const state = panelState({ tenantId: chosen, document: latest.data, failure, now });
    const notice = chosen === null ? null : refreshNotice(latest.data, failure);

    return (
        <section aria-labelledby={`${statusId}-title`} className="mt-5">
            <Card>
                <CardHeader
                    title={<span id={`${statusId}-title`}>Donations reconciliation</span>}
                    subtitle="Compares one client's copied donations with Salesforce, exactly. Changes nothing."
                />
                <div className="flex flex-col gap-4 px-5 py-4">
                    {clients.isPending ? (
                        <LoadingRows label="Loading clients…" />
                    ) : clients.isError ? (
                        <ErrorState error={clients.error} onRetry={() => void clients.refetch()} />
                    ) : (
                        <SelectField
                            label="Client"
                            value={tenantId}
                            onChange={(next) => {
                                request.reset();
                                setTenantId(next);
                            }}
                            options={options}
                            hint="The reconciliation is per client: it reads that client's Salesforce and Veevart."
                        />
                    )}

                    <p
                        id={statusId}
                        role="status"
                        aria-live="polite"
                        className="text-[13px] leading-relaxed text-content-body"
                    >
                        {stateAnnouncement(state, failure)}
                    </p>

                    {notice && (
                        <p role="note" className="text-[12.5px] text-warning-text">
                            {notice}
                        </p>
                    )}

                    {request.error && <WriteError error={request.error} />}

                    {chosen !== null && (
                        <div className="flex items-center gap-3">
                            {canWrite ? (
                                <Button
                                    onClick={() => {
                                        request.mutate();
                                    }}
                                    disabled={request.isPending || !canRequest(state, canWrite)}
                                    aria-describedby={statusId}
                                >
                                    {request.isPending ? 'Starting…' : 'Run reconciliation'}
                                </Button>
                            ) : (
                                <p className="text-[12.5px] text-content-muted">
                                    Running a reconciliation needs the engineer role. You can read
                                    the latest one here.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <PanelBody
                    state={state}
                    onRetry={() => void latest.refetch()}
                    error={latest.error}
                />
            </Card>
        </section>
    );
}

function PanelBody({
    state,
    error,
    onRetry,
}: {
    state: ReconciliationPanelState;
    error: Error | null;
    onRetry: () => void;
}) {
    switch (state.kind) {
        case 'loading':
            return <LoadingRows label="Loading the latest reconciliation…" />;
        case 'read-failed':
            return error ? <ErrorState error={error} onRetry={onRetry} /> : null;
        case 'never-run':
            return (
                <EmptyState
                    title="No reconciliation yet"
                    description="Run one after the Donations migration has copied this client's donations."
                />
            );
        case 'in-progress':
        case 'failed':
            return state.previous ? (
                <>
                    <p className="border-t border-line-subtle px-5 pt-4 text-[12.5px] text-content-muted">
                        Showing the last completed reconciliation, from{' '}
                        {formatTime(state.previous.generatedAt)}.
                    </p>
                    <ReportView report={state.previous} />
                </>
            ) : null;
        case 'completed':
            return <ReportView report={state.report} />;
        default:
            return null;
    }
}

function formatTime(iso: string): string {
    const time = Date.parse(iso);
    return Number.isFinite(time)
        ? new Date(time).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
        : iso;
}

function ReportView({ report }: { report: DonationsReconciliationReport }) {
    const id = useId();
    const calendar = fiscalCalendarNotice(report.fiscalCalendar);
    const notices = truncationNotices(report);
    const compared = moneyCompared(report);
    const totals = totalsLines(report.totals, { compared });
    const byCode = codeCounts(report);

    return (
        <div className="flex flex-col gap-6 border-t border-line-subtle px-5 py-5">
            <div className="flex flex-wrap items-center gap-3">
                <Badge tone={report.outcome === 'reconciled' ? 'success' : 'warning'}>
                    {report.outcome === 'reconciled' ? 'Reconciled' : 'Differences found'}
                </Badge>
                <span className="text-[12.5px] text-content-muted">
                    Generated {formatTime(report.generatedAt)} · request {report.requestId}
                </span>
            </div>

            <section aria-label="Counts" className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {countGroups(report).map((group) => (
                    <div key={group.title} className="rounded-lg border border-line px-4 py-3">
                        <h4 className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-content-muted">
                            {group.title}
                        </h4>
                        <dl className="flex flex-col gap-1.5">
                            {group.lines.map((line) => (
                                <div
                                    key={line.label}
                                    className="flex items-baseline justify-between gap-3 text-[13px]"
                                >
                                    <dt className="text-content-body">{line.label}</dt>
                                    <dd
                                        className={cn(
                                            'font-semibold tabular-nums',
                                            line.attention && line.value > 0
                                                ? 'text-warning-text'
                                                : 'text-content-strong',
                                        )}
                                    >
                                        {line.value.toLocaleString('en-US')}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                ))}
            </section>

            <section aria-labelledby={`${id}-totals`}>
                <h4 id={`${id}-totals`} className="mb-1 text-[14px] font-semibold text-content">
                    Totals by fiscal year and stage
                </h4>
                <p
                    className={cn(
                        'mb-3 text-[12.5px] leading-relaxed',
                        calendar.tone === 'warning' ? 'text-warning-text' : 'text-content-muted',
                    )}
                >
                    {calendar.text} Only donations the migration copied are counted.{' '}
                    {compared
                        ? "Amounts are in the donations' currency, without conversion."
                        : 'These donations use more than one currency, so only counts are compared: amounts are not compared. Every donation is still compared exactly in the differences below.'}
                </p>
                {totals.length === 0 ? (
                    <p className="text-[13px] text-content-muted">No copied donations to total.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <caption className="sr-only">
                                Salesforce and Veevart totals of copied donations, by fiscal year
                                and stage
                            </caption>
                            <thead>
                                <tr>
                                    <Th>Fiscal year</Th>
                                    <Th>Stage</Th>
                                    <Th>Salesforce (count · amount · received · refunded)</Th>
                                    <Th>
                                        Veevart (count · amount · received · refunded · pending)
                                    </Th>
                                    <Th>Veevart by status</Th>
                                    <Th>Result</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {totals.map((row) => (
                                    <tr key={row.key}>
                                        <Td>{row.fiscalYear}</Td>
                                        <Td>{row.stage}</Td>
                                        <Td className="tabular-nums">
                                            {row.salesforce.count} · {row.salesforce.amount} ·{' '}
                                            {row.salesforce.received} · {row.salesforce.refunded}
                                        </Td>
                                        <Td className="tabular-nums">
                                            {row.veevart.count} · {row.veevart.amount} ·{' '}
                                            {row.veevart.received} · {row.veevart.refunded} ·{' '}
                                            {row.veevart.remaining}
                                        </Td>
                                        <Td>{row.byStatus || '—'}</Td>
                                        <Td>
                                            <Badge tone={row.matches ? 'success' : 'danger'}>
                                                {row.verdict}
                                            </Badge>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section aria-labelledby={`${id}-issues`}>
                <h4 id={`${id}-issues`} className="mb-1 text-[14px] font-semibold text-content">
                    Differences ({report.issueCount.toLocaleString('en-US')})
                </h4>
                {notices.map((notice) => (
                    <p key={notice} role="note" className="mb-2 text-[12.5px] text-warning-text">
                        {notice}
                    </p>
                ))}
                {report.issueCount === 0 ? (
                    <p className="text-[13px] text-content-muted">
                        None: every copied donation matches Salesforce.
                    </p>
                ) : (
                    <>
                        <ul aria-label="Differences by code" className="mb-3 flex flex-col gap-1">
                            {byCode.map((entry) => (
                                <li key={entry.code} className="text-[12.5px] text-content-body">
                                    <span className="font-semibold tabular-nums">
                                        {entry.count.toLocaleString('en-US')}
                                    </span>{' '}
                                    × {entry.message}{' '}
                                    <code className="text-[11px] text-content-faint">
                                        {entry.code}
                                    </code>
                                </li>
                            ))}
                        </ul>
                        <ol aria-label="Listed differences" className="flex flex-col gap-2">
                            {report.issues.map((issue, index) => {
                                const line = issueLine(issue, index);
                                return (
                                    <li
                                        key={line.key}
                                        className="rounded-lg border border-line-subtle px-3.5 py-2.5 text-[13px]"
                                    >
                                        <p className="text-content">{line.message}</p>
                                        {line.comparison && (
                                            <p className="mt-0.5 tabular-nums text-content-body">
                                                {line.comparison}
                                            </p>
                                        )}
                                        <p className="mt-0.5 break-all text-[12px] text-content-muted">
                                            {[...line.refs, line.code].join(' · ')}
                                        </p>
                                    </li>
                                );
                            })}
                        </ol>
                    </>
                )}
            </section>

            <section aria-labelledby={`${id}-pending`}>
                <h4 id={`${id}-pending`} className="mb-1 text-[14px] font-semibold text-content">
                    Pending balances
                </h4>
                <p className="mb-3 text-[12.5px] leading-relaxed text-content-muted">
                    Copied donations Salesforce still collects. Veevart shows the balance it copied
                    and does not collect it; a payment collected in Salesforce since the copy does
                    not lower it yet.
                </p>
                {report.pendingBalances.length === 0 ? (
                    <p className="text-[13px] text-content-muted">None.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <caption className="sr-only">
                                Copied donations with a pending balance
                            </caption>
                            <thead>
                                <tr>
                                    <Th>Salesforce donation</Th>
                                    <Th>Veevart donation</Th>
                                    <Th className="text-right">Pending in Veevart</Th>
                                    <Th>Warned on its last copy</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {report.pendingBalances.map((row) => (
                                    <tr key={row.opportunityId}>
                                        <Td className="break-all">{row.opportunityId}</Td>
                                        <Td className="break-all">{row.contributionId}</Td>
                                        <Td className="text-right tabular-nums">
                                            {amount(row.remainingMinor)}
                                        </Td>
                                        <Td>{row.warned ? 'Yes' : 'No'}</Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
